import { capitalize, words } from 'lodash-es'
import type {
  Feature,
  FeatureVariant,
  FeatureVariantName,
} from '../unleash.types'

export function useFeature(name: string) {
  const runtimeConfig = useRuntimeConfig()
  const { $unleash } = useNuxtApp()

  const feature = computed<Feature | undefined>(() => {
    return $unleash.features[name]
  })

  const key = computed(() => {
    return variant.value ? `${name}:${variant.value.name}` : name
  })

  const isEnabled = computed(() => {
    return !!feature.value?.isEnabled
  })

  const title = computed(() => {
    return words(capitalize(name)).map(replaceAbbreviation).join(' ')
  })

  const url = computed(() => {
    return `${runtimeConfig.public.unleash.featureBaseUrl}${name}`
  })

  const variants = computed(() => {
    return feature.value?.variants ?? []
  })

  const variant = computed(() => {
    return feature.value && getFeatureVariantEnabled(feature.value)
  })

  const markers = computed(() => {
    return extractMarkers(feature.value?.description ?? '')
  })

  function toggle(value?: FeatureVariantName | boolean) {
    if (!feature.value) return

    toggleFeature(feature.value, value)
    $unleash.update(feature.value)
  }

  return {
    key,
    name: readonly(ref(name)),
    title,
    url,
    isEnabled,
    variant,
    variants,
    markers,
    toggle,
  }
}

function replaceAbbreviation(word: string) {
  return /srp|pdp/i.test(word) ? word.toUpperCase() : word
}

function extractMarkers(text: string) {
  return [...text.matchAll(/\[([\w-]+)\]/g)].map(([, marker]) => marker)
}

export function getFeatureVariantEnabled(
  feature: Feature,
): FeatureVariant | undefined {
  return feature.variants.find(({ isEnabled }) => isEnabled)
}

export function toggleFeature(
  feature: Feature,
  value?: FeatureVariantName | boolean,
): void {
  feature.isEnabled = value === undefined ? !feature.isEnabled : !!value
  feature.variants.forEach(
    (variant) => (variant.isEnabled = variant.name === value),
  )
}
